// Orders, receipts and subscriptions — the fetching half (busy-ai-srv#15).
//
// The three lists come from busy-main-bff, which proxies busy-timeline-srv's projection of the
// extraction facts. This module holds the request lifecycle and nothing else: every decision about
// what a row SAYS is in lib/commerce.js, which is a plain module and testable without a store.
//
// The row actions that change something (keep, archive all, mark all read) update the loaded row in
// place rather than re-fetching the list — the answer already says what the row now is, and a
// re-fetch would make a click cost a round trip it has already paid for.

import api from '@/lib/services/general.service'
import { orderRow, receiptRow, subscriptionRow } from '@/lib/commerce'

export const state = {
  orders: [],
  receipts: [],
  subscriptions: [],
  loading: { orders: false, receipts: false, subscriptions: false },
  // Which lists have actually been answered. Separate from "the list is empty": a mailbox with no
  // orders and a list nobody has asked for are both `[]`, and only the first is a count of zero the
  // Briefing may state (see reconcileCommerceCounts).
  loaded: { orders: false, receipts: false, subscriptions: false },
  // The receipts filter in force, so the export downloads what the list is showing.
  receiptFilter: { from: null, to: null, ongoingId: null },
}

export const getters = {
  orders: s => s.orders,
  orderRows: s => s.orders.map(orderRow).filter(Boolean),
  receipts: s => s.receipts,
  receiptRows: s => s.receipts.map(receiptRow).filter(Boolean),
  subscriptions: s => s.subscriptions,
  subscriptionRows: s => s.subscriptions.map(subscriptionRow).filter(Boolean),
  receiptFilter: s => s.receiptFilter,
  isLoading: s => kind => !!s.loading[kind],
  isLoaded: s => kind => !!s.loaded[kind],
  /**
   * How many things each list actually holds — the number the Briefing's row states once it is
   * known (busy-my-web#292). A list that has never been read contributes NOTHING rather than a
   * zero, so the row keeps the classification count until there is a better answer.
   */
  totals: (s) => {
    const out = {}
    if (s.loaded.orders) out.orders = s.orders.length
    if (s.loaded.receipts) out.receipts = s.receipts.length
    if (s.loaded.subscriptions) out.subscriptions = s.subscriptions.length
    return out
  },
}

export const mutations = {
  SET_ORDERS(s, orders) { s.orders = orders || [] },
  SET_RECEIPTS(s, receipts) { s.receipts = receipts || [] },
  SET_SUBSCRIPTIONS(s, subscriptions) { s.subscriptions = subscriptions || [] },
  SET_LOADING(s, { kind, value }) { s.loading = { ...s.loading, [kind]: !!value } },
  SET_LOADED(s, { kind, value }) { s.loaded = { ...s.loaded, [kind]: !!value } },
  SET_RECEIPT_FILTER(s, filter) { s.receiptFilter = { ...s.receiptFilter, ...(filter || {}) } },
  UPDATE_SUBSCRIPTION(s, updated) {
    if (!updated?.id) return
    s.subscriptions = s.subscriptions.map(row => (row.id === updated.id ? { ...row, ...updated } : row))
  },
}

export const actions = {
  async fetchOrders({ commit }, status = null) {
    commit('SET_LOADING', { kind: 'orders', value: true })
    try {
      const response = await api.get('orders', status ? { status } : {})
      commit('SET_ORDERS', unwrap(response))
      commit('SET_LOADED', { kind: 'orders', value: true })
    } catch {
      // A list that cannot be loaded is an empty list with the page's own empty state, not a toast:
      // these views are opened from a briefing entry, and a failure here is not an action's failure.
      commit('SET_ORDERS', [])
    } finally {
      commit('SET_LOADING', { kind: 'orders', value: false })
    }
  },

  async fetchReceipts({ commit }, filter = {}) {
    commit('SET_RECEIPT_FILTER', filter)
    commit('SET_LOADING', { kind: 'receipts', value: true })
    try {
      const response = await api.get('receipts', clean(filter))
      commit('SET_RECEIPTS', unwrap(response))
      commit('SET_LOADED', { kind: 'receipts', value: true })
    } catch {
      commit('SET_RECEIPTS', [])
    } finally {
      commit('SET_LOADING', { kind: 'receipts', value: false })
    }
  },

  async fetchSubscriptions({ commit }) {
    commit('SET_LOADING', { kind: 'subscriptions', value: true })
    try {
      const response = await api.get('subscriptions', {})
      commit('SET_SUBSCRIPTIONS', unwrap(response))
      commit('SET_LOADED', { kind: 'subscriptions', value: true })
    } catch {
      commit('SET_SUBSCRIPTIONS', [])
    } finally {
      commit('SET_LOADING', { kind: 'subscriptions', value: false })
    }
  },

  /**
   * Read one list unless it has already been read (busy-my-web#292).
   *
   * The Email Briefing states these three as counts before anybody opens them, and the counts it is
   * given are the classifier's THREAD counts rather than the aggregates the lists hold — so the
   * workspace asks for the aggregates on the way in and the rows say what their lists will show
   * (see reconcileCommerceCounts). Idempotent, because the workspace mounts more than once and a
   * briefing must not cost three calls each time.
   */
  ensure({ dispatch, state, rootGetters }, kind) {
    // A guest has no mailbox, so there is nothing to aggregate — the same rule the briefing's own
    // counts follow (emailSession.loadBriefing). Unknown (a store without the user module, in a
    // test) is not "signed out" and is asked.
    if (rootGetters['user/isAuthenticated'] === false) return null
    if (state.loaded[kind] || state.loading[kind]) return null
    if (kind === 'orders') return dispatch('fetchOrders', null)
    if (kind === 'receipts') return dispatch('fetchReceipts', {})
    if (kind === 'subscriptions') return dispatch('fetchSubscriptions')
    return null
  },

  /** All three, for the Briefing's three rows. Best-effort: a list that refuses keeps its count. */
  ensureAll({ dispatch }) {
    return Promise.all(['orders', 'receipts', 'subscriptions'].map(kind => dispatch('ensure', kind)))
  },

  /**
   * Archives every thread the sender is behind; the BFF fans out to the existing archive.
   *
   * Also zeroes the row's own count — every action this list offers (View emails, Archive all,
   * Mark all read) is enabled on `count > 0`, so without this the row keeps offering them on
   * emails that are already gone until the next full reload (busy-my-web#542).
   */
  async archiveAllFromSubscription({ commit }, id) {
    const response = await api.insert(`subscriptions/${id}/archive-all`, {})
    const result = unwrap(response)
    if (result) commit('UPDATE_SUBSCRIPTION', { id, archivedAll: true, count: 0 })
    return result
  },

  async markAllReadFromSubscription(_, id) {
    const response = await api.insert(`subscriptions/${id}/mark-all-read`, {})
    return unwrap(response)
  },

  /**
   * Asks the BFF for the unsubscribe URL. Nothing on any server ever FETCHES it — a one-click
   * unsubscribe acts on the user, so the user's own browser opens it.
   */
  async unsubscribe(_, id) {
    const response = await api.insert(`subscriptions/${id}/unsubscribe`, {})
    return unwrap(response)
  },

  /**
   * Downloads the receipts of the range in force as a CSV. Through axios, not a link: the export is
   * an authenticated read, and an <a href> would arrive signed out.
   */
  async exportReceipts({ state }, filter = null) {
    const range = clean(filter || state.receiptFilter)
    const response = await api.getFile('receipts/export', range)
    return { blob: response?.data, fileName: exportFileName(range) }
  },

  /** Archive one thread, through the same endpoint the mailbox uses. */
  async archiveThread(_, timelineId) {
    if (!timelineId) return false
    await api.insert('timeline/archive', { ids: [timelineId] })
    return true
  },
}

/** The name a downloaded export is saved under, stamped with the range it covers. */
export function exportFileName(filter) {
  const from = (filter?.from || 'start').toString().slice(0, 10).replace(/-/g, '')
  const to = (filter?.to || 'today').toString().slice(0, 10).replace(/-/g, '')
  return `receipts-${from}-${to}.csv`
}

/** The estate's envelope, unwrapped: { data: { data } } for a service answer, { data } for a file. */
function unwrap(response) {
  const body = response?.data
  return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
}

/** Query parameters with the empty ones left out — `from=` matches nothing and reads as a bug. */
function clean(filter) {
  const out = {}
  for (const [key, value] of Object.entries(filter || {}))
    if (value !== null && value !== undefined && value !== '') out[key] = value
  return out
}
